/**
 * The one place the frontend talks to the backend.
 *
 * Requests go to a relative `/api/...` path, which Vite proxies in dev and the
 * backend serves itself in production. Same origin either way, so the session
 * cookie stays first-party and there is no CORS or token plumbing here.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
    readonly requestId?: string | null,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** A 401 means "sign in again", which the auth provider handles specially. */
  get isUnauthenticated() {
    return this.status === 401
  }

  /** A 403 reaching the UI is a bug — design.md §P9 says we should have known. */
  get isForbidden() {
    return this.status === 403
  }
}

type Body = Record<string, unknown> | unknown[] | Blob | undefined

async function request<T>(method: string, path: string, body?: Body): Promise<T> {
  let res: Response
  try {
    res = await fetch(path, {
      method,
      credentials: 'include',
      // A file goes as itself (routers/files.py), anything else as JSON.
      headers:
        body === undefined
          ? {}
          : { 'Content-Type': body instanceof Blob ? body.type || 'application/octet-stream' : 'application/json' },
      body: body === undefined || body instanceof Blob ? body : JSON.stringify(body),
    })
  } catch {
    // Network-level failure: there is no envelope to read.
    throw new ApiError(0, 'network_error', 'Cannot reach the server. Check your connection.')
  }

  if (res.status === 204) return undefined as T

  const text = await res.text()
  let payload: unknown = null
  if (text) {
    try {
      payload = JSON.parse(text)
    } catch {
      payload = null
    }
  }

  if (!res.ok) throw failure(res, payload)

  return payload as T
}

function failure(res: Response, payload: unknown): ApiError {
  const envelope = (payload as { error?: { code?: string; message?: string; details?: unknown } })
    ?.error
  return new ApiError(
    res.status,
    envelope?.code ?? `http_${res.status}`,
    envelope?.message ?? 'The request failed.',
    envelope?.details,
    res.headers.get('x-request-id'),
  )
}

/** POST, then read the reply as Server-Sent Events, handing each `data:` JSON to
 *  `onEvent` as it arrives. A refusal before the stream starts throws as usual. */
async function stream(
  path: string,
  body: Record<string, unknown>,
  onEvent: (event: Record<string, unknown>) => void,
  signal?: AbortSignal,
): Promise<void> {
  let res: Response
  try {
    res = await fetch(path, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal,
    })
  } catch (err) {
    if (signal?.aborted) throw err
    throw new ApiError(0, 'network_error', 'Cannot reach the server. Check your connection.')
  }
  if (!res.ok || !res.body) {
    const text = await res.text()
    let payload: unknown = null
    try {
      payload = text ? JSON.parse(text) : null
    } catch {
      payload = null
    }
    throw failure(res, payload)
  }
  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader()
  let buffer = ''
  for (;;) {
    const { value, done } = await reader.read()
    if (done) return
    buffer += value
    let end: number
    while ((end = buffer.indexOf('\n\n')) >= 0) {
      const chunk = buffer.slice(0, end)
      buffer = buffer.slice(end + 2)
      if (chunk.startsWith('data: ')) onEvent(JSON.parse(chunk.slice(6)))
    }
  }
}

function query(params: Record<string, unknown> = {}) {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    search.set(key, String(value))
  }
  const qs = search.toString()
  return qs ? `?${qs}` : ''
}

export const api = {
  get: <T>(path: string, params?: Record<string, unknown>) =>
    request<T>('GET', `${path}${query(params)}`),
  post: <T>(path: string, body?: Body) => request<T>('POST', path, body),
  patch: <T>(path: string, body?: Body) => request<T>('PATCH', path, body),
  put: <T>(path: string, body?: Body) => request<T>('PUT', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
  stream,
  /** Store a file; what comes back is what an attachment field holds. */
  upload: <T>(file: File) => request<T>('POST', `/api/files${query({ name: file.name })}`, file),
}
